import { Divider, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  AutoComplete,
  AutoCompleteMulti,
  FormCheckBox,
  FormContainer,
  FormInput,
  ISelectType,
  SwitchInput,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { TTicketServiceForm, ticketServiceSchema } from "./TicketServiceSchema";
import MainApi from "../../../../../../services/mainApi.service";
import { useCallback, useEffect, useState } from "react";
import TicketServService from "../../../../../../services/ticket.service";
import SitesAppsService from "../../../../../../services/sitesApps.service";
import { SelectMessageReceivedEvent } from "./SelectMessageReceivedEvent/SelectMessageReceivedEvent";
import { BadgeColorFields } from "./BadgeColorFileds/BadgeColorFields";
import {
  ExternalChatFields,
  WHATSAPP_TICKET_SERVICE_TYPE_CODE,
} from "./ExternalChatFields/ExternalChatFields";

const ticketService = new TicketServService();

const siteAppExtensionService = new SitesAppsService();

const TICKET_CHANNELS_ROUTE =
  process.env.REACT_APP_TICKETS + "/api/v1/TicketChannels/";
const ticketChannelsService = new MainApi<ISelectType>(TICKET_CHANNELS_ROUTE);

const translationPath = "chatBot.page.register.ticketService.";

// Campos do chat interno (4uChat) só valem para Whatsapp e com Site / Aplicação preenchido
const getExternalChatValues = (v: TTicketServiceForm) => {
  const externalChatSiteApp =
    v.ticketServiceType.code === WHATSAPP_TICKET_SERVICE_TYPE_CODE
      ? v.externalChatSiteApp
      : null;

  return {
    externalChatSiteAppId: externalChatSiteApp ? externalChatSiteApp.id : null,
    fullScreenMode: externalChatSiteApp ? (v.fullScreenMode ?? false) : false,
    sendProtocolNumber: externalChatSiteApp
      ? (v.sendProtocolNumber ?? false)
      : false,
  };
};

const TicketService = () => {
  const { t } = useTranslation();
  const [siteAppOptions, setSiteAppOptions] = useState<ISelectType[]>([]);
  const [externalChatSiteAppOptions, setExternalChatSiteAppOptions] = useState<
    ISelectType[]
  >([]);

  const { sendRequest } = useFetch(
    siteAppExtensionService.siteAppExtensionList,
  );

  const loadSiteApp = useCallback(async () => {
    const { data, success } = await sendRequest({ ExtensionCode: "3" });
    if (data && success) {
      setSiteAppOptions(
        data.map((item) => ({ id: item.id, name: item.siteApp.name })),
      );
    }

    const [res80] = await Promise.all([
      siteAppExtensionService.siteAppExtensionList({ ExtensionCode: "81" }),
    ]);

    const allData = [...(Array.isArray(res80?.data) ? res80.data : [])];

    const uniqueMap = new Map<string, ISelectType>();
    allData.forEach((item) => {
      if (item.siteApp?.id) {
        uniqueMap.set(item.siteApp.id, {
          id: item.siteApp.id,
          name: item.siteApp.name,
        });
      }
    });

    setExternalChatSiteAppOptions(Array.from(uniqueMap.values()));
  }, [sendRequest]);

  useEffect(() => {
    loadSiteApp();
  }, [loadSiteApp]);

  const { sendRequest: save, loading: sLoading } = useFetch(
    ticketService.create,
  );
  const { sendRequest: update, loading: uLoading } = useFetch(
    ticketService.updateItemById,
  );
  const { sendRequest: get, loading: gLoading } = useFetch(
    ticketService.getItemById,
  );

  const handleSave = (v: TTicketServiceForm) => {
    return save({
      ...v,
      siteAppExtensions: v.siteAppExtensions.map((siteApp) => siteApp.id),
      ticketChannelInitialId: v.ticketChannelInitial.id,
      ticketChannelDonorWorkId: v.ticketChannelDonorWork
        ? v.ticketChannelDonorWork.id
        : undefined,
      ticketServiceTypeId: v.ticketServiceType.id,
      ticketMessageReceivedEventId: v.ticketMessageReceivedEvent
        ? v.ticketMessageReceivedEvent.id
        : undefined,
      ...getExternalChatValues(v),
    });
  };

  const updateHandler = (v: TTicketServiceForm, id: string) =>
    update({
      item: {
        ...v,
        siteAppExtensions: v.siteAppExtensions.map((siteApp) => siteApp.id),
        ticketChannelInitialId: v.ticketChannelInitial.id,
        ticketChannelDonorWorkId: v.ticketChannelDonorWork
          ? v.ticketChannelDonorWork.id
          : undefined,
        ticketServiceTypeId: v.ticketServiceType.id,
        ticketMessageReceivedEventId: v.ticketMessageReceivedEvent
          ? v.ticketMessageReceivedEvent.id
          : undefined,
        ...getExternalChatValues(v),
      },
      id: id,
    });

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TTicketServiceForm>> => {
      const { data, success } = await get(id);

      if (data) {
        const newData: TTicketServiceForm = {
          ...data,
          ticketChannelDonorWork: data.ticketChannelDonorWork
            ? data.ticketChannelDonorWork
            : undefined,
          siteAppExtensions: data.siteAppExtensions.map((item) => ({
            id: item.id,
            name: item.siteApp.name,
          })),
          externalChatSiteApp: data.externalChatSiteApp
            ? {
                id: data.externalChatSiteApp.id,
                name: data.externalChatSiteApp.name,
              }
            : data.externalChatSiteAppId
              ? {
                  id: data.externalChatSiteAppId,
                  name: (data as any).externalChatSiteAppName || "",
                }
              : null,
          fullScreenMode: data.fullScreenMode ?? false,
          sendProtocolNumber: data.sendProtocolNumber ?? false,
        };
        return { data: newData, success };
      }
      return { data, success };
    },
    [get],
  );

  const loading = sLoading || uLoading || gLoading;

  return (
    <FormContainer<TTicketServiceForm>
      saveFunction={handleSave}
      updateFunction={updateHandler}
      getFunction={getHandler}
      loading={loading}
      defaultValues={{
        allowDistributionList: false,
        showBadgeNameColor: false,
        fullScreenMode: false,
        sendProtocolNumber: false,
      }}
      title={t(translationPath + "ticket_service")}
      schema={ticketServiceSchema}
      subtitle={t(translationPath + "new_ticket_service")}
      subtitleWatchField={"name"}
      fields={() => (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} textAlign={"end"}>
            <SwitchInput
              name="allowDistributionList"
              label={t(translationPath + "allowDistributionList")}
            />
            <SwitchInput name="enable" label={t(translationPath + "enable")} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormInput
              size="small"
              name="name"
              fullWidth
              label={t(translationPath + "name") || "Name"}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <AutoComplete
              getOptionLabel={(option) => {
                return option.name;
              }}
              size="small"
              label={t(translationPath + "ticket_service_type")}
              name="ticketServiceType"
              request={ticketService.list}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <AutoComplete
              getOptionLabel={(option) => {
                return option.name;
              }}
              size="small"
              label={t(translationPath + "ticket_channel_initial")}
              name="ticketChannelInitial"
              request={ticketChannelsService.list}
            />
          </Grid>

          <SelectMessageReceivedEvent />

          <Grid item xs={12} sm={6}>
            <AutoComplete
              getOptionLabel={(option) => {
                return option.name;
              }}
              size="small"
              label={t(translationPath + "ticket_channel_donor_work")}
              name="ticketChannelDonorWork"
              request={ticketChannelsService.list}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <AutoCompleteMulti
              size="small"
              label={t(translationPath + "site_app_extensions")}
              name="siteAppExtensions"
              options={siteAppOptions}
            />
          </Grid>

          <ExternalChatFields options={externalChatSiteAppOptions} />

          <Grid item xs={12} sm={12}>
            <FormCheckBox
              name="showMessageDisableArea"
              label={`${t(translationPath + "show_message_disable_area")}`}
            />
          </Grid>

          <Grid item xs={12} sm={12}>
            <Divider sx={{ my: 4, width: "100%" }} />

            <Typography variant="subtitle2" fontWeight="bold">
              {t(translationPath + "config_title")}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={12}>
            <SwitchInput
              name="showBadgeNameColor"
              label={t(translationPath + "show_badge_name_color")}
            />
          </Grid>

          <BadgeColorFields />
        </Grid>
      )}
    />
  );
};

export default TicketService;
